import {FilterFacetsService} from '../shared/filter-facets.service';
import {hasFilterValue, numericFilterCount, registrationFilterCount} from '../shared/filter-section-state';
import {CommonModule} from '@angular/common';
import {Component, inject, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {FilterMultiselectComponent, selectionValues} from '../shared/filter-multiselect.component';
import {of} from 'rxjs';
import {catchError, finalize} from 'rxjs/operators';

import {MaterialModule} from '../../../material.module';
import {Filter} from '../../../models/filter.model';
import {ClientLoadingService} from '../../../services/auth/client-loading.service';
import {ClientService} from '../../../services/auth/client.service';
import {FilterService} from '../../../services/enrich/filter.service';
import {ToastService} from '../../../services/layout/toast.service';
import {BreadcrumbsComponent} from '../../../shared/breadcrumbs/breadcrumbs.component';
import {EnrichConsoleDiagnosticsService} from '../../../services/enrich';
import {EnrichSetupService} from '../../../services/enrich/enrich-setup.service';

@Component({
  selector: 'app-enrich-filter-form',
  templateUrl: './filter-form.component.html',
  styleUrls: ['./filter-form.component.scss'],
  host: {class: 'webinars-m3 webinars-shell'},
  standalone: true,
  providers: [FilterFacetsService],
  imports: [CommonModule, MaterialModule, ReactiveFormsModule, FilterMultiselectComponent, BreadcrumbsComponent]
})
export class EnrichFilterFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private filterService = inject(FilterService);
  private clientService = inject(ClientService);
  private clientLoadingService = inject(ClientLoadingService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private toastService = inject(ToastService);
    private enrichDiagnostics = inject(EnrichConsoleDiagnosticsService);
    private enrichSetupService = inject(EnrichSetupService);

  get localSearchOnly(): boolean {
    return this.enrichSetupService.getCurrentReadiness().localSearchOnly === true;
  }

  filterForm: FormGroup;
  get registrationFilterCount(): number { return registrationFilterCount(this.filterForm.get('criteria')?.value || {}); }
  get numericFilterCount(): number { return numericFilterCount(this.filterForm.get('criteria')?.value || {}, this.numericFields); }
  isLoading = false;
  isSaving = false;
  isRunning = false;
  isDeleting = false;
  error: string | null = null;
  editingFilter: Filter | null = null;

  readonly facets = inject(FilterFacetsService);
  get filterOptions() { return this.facets.options(); }
  regionSelectionChanged = false;
  readonly multiselectFields = ['nace_code1', 'number_of_employees_group', 'municipality', 'region', 'organization_type'];

  // Numeric fields that support comparison operators
  numericFields: string[] = ['number_of_employees', 'latest_annual_revenue', 'latest_ebit', 'latest_sum_equity'];

  // Standard filter fields
  standardFilterFields: string[] = [
    'company_id',
    'city',
    'municipality',
    'postal_code',
    'region',
    'organization_type',
    'accounting_year',
    'vat_registered',
    'in_company_register',
    'in_foundation_register',
    'in_voluntary_register',
    'in_party_register'
  ];

  comparisonOperators = [
    {value: 'eq', label: 'Equal to (=)'},
    {value: 'gt', label: 'Greater than (>)'},
    {value: 'lt', label: 'Less than (<)'},
    {value: 'gte', label: 'Greater than or equal to (>=)'},
    {value: 'lte', label: 'Less than or equal to (<=)'},
    {value: 'between', label: 'Between'}
  ];

  constructor() {
    this.filterForm = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      enabled: [{value: !this.localSearchOnly, disabled: this.localSearchOnly}],
      criteria: this.fb.group({
        // Existing fields
        nace_code1: [[]],
        number_of_employees_group: [[]],
        postal_code: [''],
        region: [[]]
      })
    });

    const criteriaGroup = this.filterForm.get('criteria') as FormGroup;

    this.numericFields.forEach(field => {
      criteriaGroup.addControl(
        field,
        this.fb.group({
          operator: ['eq'],
          value: [''],
          min: [''],
          max: ['']
        })
      );
    });

    this.standardFilterFields.forEach(field => {
      if (!criteriaGroup.contains(field)) {
        criteriaGroup.addControl(field, this.fb.control(this.multiselectFields.includes(field) ? [] : ''));
      }
    });
  }

  get isEditMode(): boolean {
    return !!this.route.snapshot.paramMap.get('id');
  }

  get breadcrumbs(): Array<{label: string; route?: string}> {
    const label = this.editingFilter
      ? (this.filterForm.get('name')?.value || this.editingFilter.name)
      : this.isEditMode
        ? 'Filter'
        : 'New filter';

    return [
      {label: 'Shine Enrich', route: '/enrich/setup'},
      {label: 'Company data', route: '/enrich/companies'},
      {label: 'Imports', route: '/enrich/filters'},
      {label}
    ];
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.error = null;

    this.clientLoadingService.ensureClientSelected().subscribe({
      next: () => {
        this.loadFilterOptions();
        const filterId = this.route.snapshot.paramMap.get('id');
        if (filterId) {
          this.loadFilter(filterId);
        } else {
          this.isLoading = false;
        }
      },
      error: err => {
        this.enrichDiagnostics.reportError('enrich.filter-form.client-selection', err);
        this.error = 'No client selected. Please select a client from the dropdown.';
        this.isLoading = false;
      }
    });

  }

  loadFilterOptions(): void {
    const clientId = this.clientService.client?.id;
    if (!clientId) return;
    this.facets.connect(this.filterForm.get('criteria')!.valueChanges, () => this.getSearchParams(), clientId, options => {
      if (!this.regionSelectionChanged) return;
      this.regionSelectionChanged = false;
      if (!selectionValues(this.filterForm.get('criteria.region')?.value).length) return;
      const control = this.filterForm.get('criteria.municipality')!;
      const selected = selectionValues(control.value);
      const allowed = new Set(options.municipality.map(option => option.value));
      const remaining = selected.filter(value => allowed.has(value));
      if (remaining.length !== selected.length) {
        control.setValue(remaining);
        this.toastService.showInfo('Municipality selections were adjusted to the selected regions.', 4000);
      }
    });
  }

  loadFilter(id: string): void {
    const clientId = this.clientService.client?.id;
    if (!clientId) {
      this.error = 'No client selected';
      this.isLoading = false;
      return;
    }

    this.filterService
      .getFilterById(id, clientId)
      .pipe(
        catchError(error => {
          this.enrichDiagnostics.reportError('enrich.filter-form.load-filter', error);
          this.error = 'Failed to load filter';
          return of(null);
        }),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe(filter => {
        if (!filter) {
          return;
        }
        this.editingFilter = filter;
        this.applyFilterToForm(filter);
      });
  }

  private applyFilterToForm(filter: Filter): void {
    this.filterForm.patchValue({
      name: filter.name,
      description: filter.description,
      enabled: this.localSearchOnly ? false : filter.enabled
    });

    const criteriaGroup = this.filterForm.get('criteria') as FormGroup;

    Object.keys(criteriaGroup.controls).forEach(key => {
      if (this.numericFields.includes(key)) {
        (criteriaGroup.get(key) as FormGroup).reset({operator: 'eq', value: '', min: '', max: ''});
      } else {
        criteriaGroup.get(key)?.setValue(this.multiselectFields.includes(key) ? [] : '');
      }
    });

    if (!filter.criteria) {
      return;
    }

    Object.keys(filter.criteria).forEach(key => {
      if (this.numericFields.includes(key)) {
        const value = filter.criteria[key];
        if (value === null || value === undefined) {
          return;
        }

        const numericGroup = criteriaGroup.get(key) as FormGroup;
        if (typeof value === 'object') {
          if (value.between) {
            numericGroup.patchValue({operator: 'between', min: value.between[0], max: value.between[1]});
          } else if (value.gt !== undefined) {
            numericGroup.patchValue({operator: 'gt', value: value.gt});
          } else if (value.lt !== undefined) {
            numericGroup.patchValue({operator: 'lt', value: value.lt});
          } else if (value.gte !== undefined) {
            numericGroup.patchValue({operator: 'gte', value: value.gte});
          } else if (value.lte !== undefined) {
            numericGroup.patchValue({operator: 'lte', value: value.lte});
          }
        } else {
          numericGroup.patchValue({operator: 'eq', value});
        }

        return;
      }

      criteriaGroup.get(key)?.setValue(this.multiselectFields.includes(key) ? selectionValues(filter.criteria[key]) : filter.criteria[key]);
    });
  }

  getSearchParams() {
    const filterData = {criteria: JSON.parse(JSON.stringify(this.filterForm.get('criteria')?.value || {}))};
    if (filterData.criteria) {
      this.numericFields.forEach(field => {
        const fieldData = filterData.criteria[field];
        if (!fieldData || typeof fieldData !== 'object') {
          return;
        }

        const {operator, value, min, max} = fieldData;
        let comparisonValue: any;

        switch (operator) {
          case 'eq':
            comparisonValue = hasFilterValue(value) ? Number(value) : undefined;
            break;
          case 'gt':
            comparisonValue = hasFilterValue(value) ? {gt: Number(value)} : undefined;
            break;
          case 'lt':
            comparisonValue = hasFilterValue(value) ? {lt: Number(value)} : undefined;
            break;
          case 'gte':
            comparisonValue = hasFilterValue(value) ? {gte: Number(value)} : undefined;
            break;
          case 'lte':
            comparisonValue = hasFilterValue(value) ? {lte: Number(value)} : undefined;
            break;
          case 'between':
            if (hasFilterValue(min) && hasFilterValue(max) && Number(min) > Number(max)) {
              this.error = `For field "${field}", minimum value must be less than maximum value`;
              filterData.criteria[field] = undefined;
              return;
            }
            comparisonValue = hasFilterValue(min) && hasFilterValue(max) ? {between: [Number(min), Number(max)]} : undefined;
            break;
          default:
            comparisonValue = undefined;
        }

        filterData.criteria[field] = comparisonValue;
      });

      Object.keys(filterData.criteria).forEach(key => {
        const value = filterData.criteria[key];
        if (
          value === '' ||
          value === null ||
          value === undefined ||
          (typeof value === 'object' && Object.keys(value).length === 0)
        ) {
          delete filterData.criteria[key];
        }
      });
    }

    return filterData.criteria;
  }

  saveFilter(): void {
    if (this.filterForm.invalid) {
      this.error = 'Please fix the form errors before saving';
      return;
    }

    const clientId = this.clientService.client?.id;
    if (!clientId) {
      this.error = 'No client selected';
      return;
    }

    this.isSaving = true;
    this.error = null;
    const filterId = this.route.snapshot.paramMap.get('id');
    const formValue = this.filterForm.value;
    const filterData = JSON.parse(JSON.stringify(formValue));
    if (this.localSearchOnly) filterData.enabled = false;

    filterData.criteria = this.getSearchParams();
    if (this.error) { this.isSaving = false; return; }

    const request$ = filterId
      ? this.filterService.updateFilter(filterId, filterData, clientId)
      : this.filterService.createFilter(filterData, clientId);

    request$
      .pipe(
          catchError(_error => {
          this.error = filterId ? 'Failed to update filter' : 'Failed to create filter';
          return of(null);
        }),
        finalize(() => {
          this.isSaving = false;
        })
      )
      .subscribe(saved => {
        if (!saved) {
          return;
        }

        const message = filterId ? 'Filter updated successfully' : 'Filter created successfully';
        this.router.navigate(['/enrich/filters'], {state: {successMessage: message}});
      });
  }

  runCurrentFilter(): void {
    if (this.localSearchOnly) return;
    if (!this.editingFilter?.id) {
      return;
    }

    const clientId = this.clientService.client?.id;
    if (!clientId) {
      this.error = 'No client selected';
      return;
    }

    this.isRunning = true;
    this.error = null;
    this.filterService
      .runFilter(this.editingFilter.id, clientId)
      .pipe(
        catchError(error => {
          this.enrichDiagnostics.reportError('enrich.filter-form.run-filter', error);
          this.error = `Failed to run filter ${this.editingFilter?.name ?? ''}`.trim();
          return of(null);
        }),
        finalize(() => {
          this.isRunning = false;
        })
      )
      .subscribe(response => {
        if (!response) {
          return;
        }

        this.toastService.showSuccess('Filter executed successfully', 4000);
      });
  }

  deleteCurrentFilter(): void {
    if (!this.editingFilter?.id) {
      return;
    }

    const confirmed = confirm(`Are you sure you want to delete the filter "${this.editingFilter.name}"?`);
    if (!confirmed) {
      return;
    }

    const clientId = this.clientService.client?.id;
    if (!clientId) {
      this.error = 'No client selected';
      return;
    }

    this.isDeleting = true;
    this.error = null;
    this.filterService
      .deleteFilter(this.editingFilter.id, clientId)
      .pipe(
        catchError(error => {
          this.enrichDiagnostics.reportError('enrich.filter-form.delete-filter', error);
          this.error = `Failed to delete filter ${this.editingFilter?.name ?? ''}`.trim();
          return of(null);
        }),
        finalize(() => {
          this.isDeleting = false;
        })
      )
      .subscribe(response => {
        if (response === null) {
          return;
        }

        this.router.navigate(['/enrich/filters'], {
          state: {successMessage: 'Filter deleted successfully'}
        });
      });
  }

  cancel(): void {
    this.router.navigate(['/enrich/filters']);
  }
}
